import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToastStore } from "../store/toast";
import { DEMO_MODE } from "../lib/config";
import { api } from "../lib/api";

interface Form {
  firstName: string;
  middleName: string;
  lastName: string;
  dob: string;
  ssn: string;
  phone: string;
  email: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  employer: string;
  employmentLength: string;
  annualIncome: string;
  monthlyHousing: string;
}

const EMPTY: Form = {
  firstName: "",
  middleName: "",
  lastName: "",
  dob: "",
  ssn: "",
  phone: "",
  email: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  employer: "",
  employmentLength: "",
  annualIncome: "",
  monthlyHousing: "",
};

function normalizeSsn(s: string): string {
  const digits = s.replace(/\D/g, "");
  if (digits.length !== 9) return "";
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

export function PatientNewPage() {
  const [form, setForm] = useState<Form>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof Form, string>>>({});
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.show);

  const update = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const validate = (): boolean => {
    const e: Partial<Record<keyof Form, string>> = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!form.dob) e.dob = "Required";
    const ssn = normalizeSsn(form.ssn);
    if (!ssn) e.ssn = "Must be 9 digits";
    if (!form.phone.trim()) e.phone = "Required";
    if (form.email && !/.+@.+\..+/.test(form.email)) e.email = "Invalid email";
    if (!form.zip.trim()) e.zip = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim(),
        lastName: form.lastName.trim(),
        dob: form.dob,
        ssn: normalizeSsn(form.ssn),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: {
          street: form.street.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          zip: form.zip.trim(),
        },
        employer: form.employer.trim(),
        employmentLength: form.employmentLength.trim(),
        annualIncome: Number(form.annualIncome) || 0,
        monthlyHousing: Number(form.monthlyHousing) || 0,
      };
      if (!DEMO_MODE) {
        const created = await api.post<{ id: string }>("/patients", payload);
        showToast("Patient added", "ok");
        navigate(`/sessions/new?patientId=${created.id}`);
      } else {
        showToast("Patient added (demo)", "ok");
        navigate("/patients");
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to save", "warn");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Add patient</h1>
          <p className="page__sub">Manual entry. v1 doesn't import from a PMS.</p>
        </div>
      </div>

      <div className="form">
        <div className="form__grid">
          <Input label="First name" value={form.firstName} onChange={(v) => update("firstName", v)} err={errors.firstName} />
          <Input label="Middle name" value={form.middleName} onChange={(v) => update("middleName", v)} />
          <Input label="Last name" value={form.lastName} onChange={(v) => update("lastName", v)} err={errors.lastName} />
          <Input label="Date of birth" type="date" value={form.dob} onChange={(v) => update("dob", v)} err={errors.dob} />
          <Input
            label="Social Security Number"
            value={form.ssn}
            onChange={(v) => update("ssn", v)}
            placeholder="123-45-6789"
            err={errors.ssn}
            hint="Encrypted at rest. Reveal is audit-logged."
          />
          <Input label="Mobile phone" value={form.phone} onChange={(v) => update("phone", v)} err={errors.phone} />
          <Input label="Email" type="email" value={form.email} onChange={(v) => update("email", v)} err={errors.email} />
          <div />
          <Input label="Street" value={form.street} onChange={(v) => update("street", v)} className="form__row--full" />
          <Input label="City" value={form.city} onChange={(v) => update("city", v)} />
          <Input label="State" value={form.state} onChange={(v) => update("state", v)} />
          <Input label="ZIP" value={form.zip} onChange={(v) => update("zip", v)} err={errors.zip} />
          <div />
          <Input label="Employer" value={form.employer} onChange={(v) => update("employer", v)} />
          <Input label="Years employed" value={form.employmentLength} onChange={(v) => update("employmentLength", v)} />
          <Input label="Annual gross income" type="number" value={form.annualIncome} onChange={(v) => update("annualIncome", v)} />
          <Input label="Monthly housing payment" type="number" value={form.monthlyHousing} onChange={(v) => update("monthlyHousing", v)} />
        </div>
        <div className="form__actions">
          <button className="btn btn--ghost" onClick={() => navigate(-1)} disabled={busy}>Cancel</button>
          <button className="btn btn--solid" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : "Save and start session"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface InputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  err?: string;
  hint?: string;
  className?: string;
}

function Input({ label, value, onChange, type = "text", placeholder, err, hint, className }: InputProps) {
  return (
    <label className={`form__row ${className ?? ""}`}>
      <span className="form__label">{label}</span>
      <input
        className="form__input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="form__hint">{hint}</span>}
      {err && <span className="form__error">{err}</span>}
    </label>
  );
}
